import type { CollapsePlacement } from './collapse';

/**
 * The two-column grid, as data so it can be unit-tested.
 *
 * ⚠️ LEFT-FIRST WHEN STACKED IS SOURCE ORDER, NOT A TRICK.
 * The `left` slot is rendered first in the DOM in BOTH variants; only the track template
 * differs. There is deliberately no `order-*` utility in either string — `order-*`
 * desynchronises visual order from tab order and screen-reader order, which is a real
 * accessibility regression to solve a problem source order never had.
 * `twoColumn.test.ts` asserts their absence.
 *
 * `items-start` is load-bearing: grid items stretch by default, and a stretched item can
 * never be `position: sticky`.
 */
export type TwoColumnVariant = 'detail' | 'form' | 'tree';

export const TWO_COLUMN_GRID: Record<TwoColumnVariant, string> = {
  /** Narrow left carrying identity and attributes; wide right carrying the substance. */
  detail: 'grid grid-cols-1 items-start gap-6 @min-[880px]:grid-cols-[400px_minmax(0,1fr)]',
  /**
   * The mirror: wide left for the fields, narrow right for a live summary.
   *
   * ⚠️ [amended P2] THE `xl` STEP IS ARITHMETIC, NOT TASTE.
   *
   * P2's policy builder is this variant's first real consumer, and its right column
   * carries a live preview of the requirement GRID. At 360px the usable width after the
   * preview card's border and padding is ~328px; a "TOTAL REQUIRED" row-label gutter at
   * `text-xs` needs ~92px; that leaves ~236px, which at a legible 56px per numeric
   * column is FOUR columns. PA_REQUIREMENTS §6.1's own worked example has five (four
   * posts plus Vehicle).
   *
   * 440px yields ~316px of column space — five to six. `lg` is left untouched, so the
   * 360px decision is preserved exactly where it was made, and below `xl` the preview
   * scrolls horizontally with truncated labels carrying the full name in `title`.
   */
  form: 'grid grid-cols-1 items-start gap-6 @min-[920px]:grid-cols-[minmax(0,1fr)_400px] @min-[1120px]:grid-cols-[minmax(0,1fr)_440px]',
  /**
   * [P7B.1 §2] A navigation tree beside the pane it opens — `detail`'s shape with a narrower left,
   * the review site's 320px. The tree is the secondary column: it sticks, scrolls on its own, and
   * below the split it is the bar "Browse all N templates" over the pane.
   */
  tree: 'grid grid-cols-1 items-start gap-6 @min-[880px]:grid-cols-[320px_minmax(0,1fr)]',
};

/** Which slot sticks on desktop. The secondary column, in both variants. */
export const TWO_COLUMN_STICKY: Record<TwoColumnVariant, 'left' | 'right'> = {
  detail: 'left',
  form: 'right',
  tree: 'left',
};

/**
 * ⚠️ [amended UX-FINAL] PER VARIANT, BECAUSE THE VARIANTS SPLIT AT DIFFERENT WIDTHS.
 *
 * This was ONE string pinned at 880 while `form` splits at 920, so between 880 and 919px of
 * content the form's right slot was `position: sticky` AND viewport-capped AND still a
 * full-width stacked block — the nested-scroll trap P4-UX removed from `PolicyBuilder`,
 * reintroduced in a 40px band. In window terms that is 1184–1223 with the sidebar out and
 * 984–1023 with the rail: exactly the "roughly 1024 to 1280" the gap reported.
 *
 * ⚠️ `sticky` IS NO LONGER THRESHOLD-GATED, AND THAT IS THE COLLAPSE. Above the split it is
 * a column riding the scroll; below it, a summary bar on the fold. Both are `position:
 * sticky`; what changes at the threshold is the EDGE:
 *
 *   detail — the secondary is the FIRST child, so it anchors to the TOP and the substance
 *            scrolls under it. `bottom-0` on a first child floats it down over its own
 *            sibling and leaves a hole where it used to be.
 *   form   — the secondary is the LAST child, so it anchors to the BOTTOM and the fields
 *            scroll behind it. `top-0` on a last child never engages: it is already past
 *            everything, so it would "stick" only at the very end of the scroll.
 *
 * MEASURED in Chromium before this was built, because the obvious objection is that a
 * stacked grid item is alone in an auto row and therefore has no sticky travel: it pins
 * exactly as block flow does. The control in that probe — a wrapper the element's own
 * height — is the case where sticky really is inert, and it behaves visibly differently.
 * So `TWO_COLUMN_GRID` needs no `@max-…:block` and is untouched.
 *
 * Each range names ONE edge and resets the other, so the two constraints never both apply.
 */
export const STICKY_SLOT: Record<TwoColumnVariant, string> = {
  detail: 'sticky z-20 bottom-auto @max-[880px]:top-0 @min-[880px]:top-4',
  form: 'sticky z-20 @max-[920px]:top-auto @max-[920px]:bottom-0 @min-[920px]:top-4 @min-[920px]:bottom-auto',
  /* `detail`'s: the tree is the FIRST child, so it hangs from the top. */
  tree: 'sticky z-20 bottom-auto @max-[880px]:top-0 @min-[880px]:top-4',
};

/**
 * ⚠️ [amended UX-FINAL] ONE CAP FOR EVERY STICKY SECONDARY COLUMN, AND THE PRIMITIVE OWNS IT.
 *
 * P4-UX collapsed two hand-approximated caps that disagreed with each other — `IntakeForm`'s
 * and `PolicyBuilder`'s — into one constant. This finishes that job by moving it off the
 * pages entirely. A page cannot apply it correctly, because the correct threshold is the
 * VARIANT's and the page does not know it: both sites imported a constant pinned at 880 onto
 * a layout that splits at 920, and `ArrangementDetail` applied no cap at all, so `detail`'s
 * sticky identity column was unreachable at its bottom whenever it exceeded the viewport.
 * That is a defect nobody reported, fixed by moving the constant rather than by copying it a
 * third time.
 *
 * `--pams-chrome` is declared in `styles/index.css`. ⚠️ [amended P5c] It is now a SINGLE
 * value: a `:has()` rule used to raise it whenever the OTP bypass banner sat above the
 * shell, and §11 removed that banner. Either way this stays pure CSS — no measurement, no
 * `ResizeObserver`.
 * `100dvh` rather than `100vh` so a mobile URL bar does not push the foot off screen.
 *
 * `60dvh` below the split is a JUDGEMENT, not arithmetic: the panel floats over the page, so
 * it may not own the screen. It is the number to look at first in a browser.
 */
export const TWO_COLUMN_COLLAPSE: Record<TwoColumnVariant, CollapsePlacement> = {
  detail: {
    root: '@max-[880px]:overflow-hidden @max-[880px]:rounded-b-lg @max-[880px]:shadow-lg',
    // The bar's boundary with the page is its BOTTOM edge, because it hangs from the top.
    bar: 'border-b @min-[880px]:hidden',
    body: 'min-w-0 overflow-y-auto @max-[880px]:max-h-[60dvh] @min-[880px]:max-h-[calc(100dvh-var(--pams-chrome))]',
    collapsed: '@max-[880px]:hidden',
  },
  form: {
    root: '@max-[920px]:overflow-hidden @max-[920px]:rounded-t-lg @max-[920px]:shadow-lg',
    // …and `form`'s is its TOP edge, because it sits on the fold. Same rule, mirrored.
    bar: 'border-t @min-[920px]:hidden',
    body: 'min-w-0 overflow-y-auto @max-[920px]:max-h-[60dvh] @min-[920px]:max-h-[calc(100dvh-var(--pams-chrome))]',
    collapsed: '@max-[920px]:hidden',
  },
  /* `detail`'s, at `detail`'s split: the tree scrolls on its own above it and opens in place below. */
  tree: {
    root: '@max-[880px]:overflow-hidden @max-[880px]:rounded-b-lg @max-[880px]:shadow-lg',
    bar: 'border-b @min-[880px]:hidden',
    body: 'min-w-0 overflow-y-auto @max-[880px]:max-h-[60dvh] @min-[880px]:max-h-[calc(100dvh-var(--pams-chrome))]',
    collapsed: '@max-[880px]:hidden',
  },
};

/**
 * ⚠️ [amended D4.1] THE GRID WHILE A DISCLOSED SECONDARY COLUMN IS CLOSED.
 *
 * One column, and — the part that matters — NO RESERVED TRACK. Class-hiding the slot alone
 * is not enough: the variant template names a `400px` track, so a `display: none` grid item
 * leaves the track sitting there empty and the form is exactly as narrow as before. The
 * template has to change with it.
 *
 * ⚠️ IT CARRIES NO THRESHOLD NUMBER, and that is deliberate rather than an omission. A
 * disclosure is STATE, not width: the column is closed at 1366 and closed at 2560 until the
 * operator asks for it. `twoColumn.test.ts`'s set-equality guard reads the numbers out of the
 * variant strings, so a number here would have to be a third split nobody declared.
 */
export const TWO_COLUMN_CLOSED = 'grid grid-cols-1 items-start gap-6';

/** Overridable per call site; a bar that says only "Summary" is a bar nobody reads. */
export const TWO_COLUMN_SECONDARY_LABEL: Record<TwoColumnVariant, string> = {
  detail: 'Details',
  form: 'Summary',
  tree: 'Browse',
};

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  [C1f §5] THE GRID FILLS ITS ROW, SO A CHILD CAN OWN THE OVERFLOW.
 *
 *  ⚠️ `grid-rows-[minmax(0,1fr)]` IS THE MECHANISM AND `h-full` ALONE IS NOT ENOUGH.
 *  A grid whose only row is `auto` sizes that row to its tallest item, so `h-full` on the
 *  item resolves against the item's own content height — circular, and it bounds nothing.
 *  `minmax(0,1fr)` fixes the row at the GRID's height instead, and the grid's height comes
 *  from the `<Form>` above it, which is `min-h-0 flex-1 overflow-y-auto` and therefore
 *  definite. `minmax(0,…)` rather than `1fr` because a bare `1fr` may not shrink below its
 *  content — the same reason `ui/dialog.tsx`'s preview grid spells it out.
 *
 *  ⚠️ `self-stretch` ON THE PRIMARY SLOT, NEVER `items-stretch` ON THE GRID. This file's own
 *  header records that `items-start` is load-bearing because a STRETCHED item can never be
 *  `position: sticky`, and the SECONDARY column is sticky in both variants. Stretching one
 *  item leaves the other exactly as it was.
 *
 *  ⚠️ AND NEITHER CARRIES A THRESHOLD NUMBER OR A WINDOW BREAKPOINT — `TWO_COLUMN_CLOSED`'s
 *  reason verbatim. Filling is STATE (which step the host is on), not width, and
 *  `twoColumn.test.ts`'s set-equality guard would read a number here as a split nobody
 *  declared.
 * ─────────────────────────────────────────────────────────────────────────────────
 */
export const TWO_COLUMN_FILL = 'h-full min-h-0 grid-rows-[minmax(0,1fr)]';
export const TWO_COLUMN_FILL_SLOT = 'min-h-0 self-stretch';

/**
 * ⚠️ [C1f §5] A BODY IN PAGE FLOW, SO IT CHAINS AT BOTH ENDS.
 *
 * ⚠️ THERE IS NO `overscroll-contain` HERE AND THERE MUST NOT BE, WHICH IS THE OPPOSITE OF
 * `AppSidebar`'s idiom one file over. [C1e §1a] drew the distinction: `overscroll-behavior:
 * contain` prevents chaining AT THE BOUNDARY, which is exactly "trapping the wheel at either
 * end". A full-height fixed column wants that, because chaining would scroll the page behind
 * navigation somebody is reading. A duty-point list sits IN THE FORM, and reaching its end is
 * a natural continuation into the rest of the page. `IntakeForm.test.tsx` carries a negative
 * control so the two strings cannot be "harmonised".
 */
export const SECTION_SCROLL_BODY = 'min-h-0 flex-1 overflow-y-auto';
